import { agreementRequest } from "utils/rctiAgreementAPI";
import { agreementReference } from "./RctiDesignSystem";
import { isSignedAgreement } from "utils/rctiAgreementSigned";
import { rctiTimestamp } from "utils/rctiTimestamp";
/* eslint-disable react-hooks/exhaustive-deps */
import { savePdfResponse } from "utils/documentPdf";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useMaterialUIController } from "context";
import AppHeader from "components/AppHeader";
import PageSkeleton from "components/Skeleton/PageSkeleton";
import { DashboardNavigation } from "layouts/businessUserDashboard";
import DebitNoteResponsiveHeader from "layouts/DebitNote/DebitNoteResponsiveHeader";
import { fetchActivityLog, saveNotes } from "utils/api";
import { getRCTIByID, downloadRCTI, resendEmailRcti } from "utils/invoiceAPI";
import { getEmailAndTemplateForQuotation } from "utils/quotationAPI";
import { showResMsg } from "utils/generic";
import { showDraftSavedMessage } from "components/InlineFeedback/DraftSaveConfirmation";
import {
  LuArrowLeft,
  LuCircleCheck,
  LuDownload,
  LuExternalLink,
  LuFileText,
  LuHistory,
  LuLockKeyhole,
  LuMail,
  LuReceipt,
  LuSend,
  LuWallet,
  LuTriangleAlert,
  LuRefreshCw,
} from "react-icons/lu";
import SendInvoiceModal from "layouts/Invoices/SendInvoiceModal";
import RCTISettlementDialog from "./RCTISettlementDialog";
import "layouts/Invoices/invoiceFlow.css";
import "./rctiDesign.css";
import { downloadRctiAttachment } from "utils/rctiAttachmentAPI";
import RCTIDocument from "./RCTIDocument";
import "./rctiDetail.css";
import "./rctiDocumentJourney.css";
import "./rctiIssuedDetail.css";
import "./rctiResponsive.css";

const moneyValue = (value) =>
  Number(String(value ?? 0).replace(/[^0-9.-]/g, "")) || 0;
const dateValue = (
  value,
  options = { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }
) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : new Intl.DateTimeFormat("en-AU", options).format(date);
};

function ViewRCTI() {
  const { id: rctiID } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [controller] = useMaterialUIController();
  const darkMode = Boolean(controller.darkMode);
  const [navOpen, setNavOpen] = useState(false);
  const [loginDetails, setLoginDetails] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("LOGIN_DETAILS") || "null");
    } catch {
      return null;
    }
  });
  const [values, setValues] = useState({});
  const [agreement, setAgreement] = useState(null);
  useEffect(() => {
    let mounted = true;
    setAgreement(null);
    if (values.rctiAgreementID)
      agreementRequest("", undefined, "get")
        .then((data) => {
          if (mounted)
            setAgreement(
              data.agreements?.find(
                (row) => String(row._id) === String(values.rctiAgreementID)
              ) || null
            );
        })
        .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [values.rctiAgreementID]);
  const [items, setItems] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [activityRows, setActivityRows] = useState([]);
  const [activityLoadError, setActivityLoadError] = useState(false);
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [settlementOpen, setSettlementOpen] = useState(false);
  const [settlementResult, setSettlementResult] = useState(null);
  const [internalNote, setInternalNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [communicationOpen, setCommunicationOpen] = useState(false);
  const [communicationPhase, setCommunicationPhase] = useState("review");
  const [templateValues, setTemplateValues] = useState(null);

  const paymentHistory = useMemo(() => {
    const candidates = [
      values.rctiPayment,
      values.rctiPayments,
      values.paymentHistory,
      values.payments,
    ];
    return candidates.find(Array.isArray) || [];
  }, [values]);

  const loadDetails = useCallback(() => {
    if (!rctiID) return;
    setLoadingPage(true);
    setLoadError(null);
    getRCTIByID(
      { rctiID },
      (response) => {
        const record = response?.Result_Output?.[0];
        if (response?.Result_Status && record) {
          setValues(record);
          setItems(Array.isArray(record.itemsList) ? record.itemsList : []);
          setDocuments(
            Array.isArray(record.documentsList) ? record.documentsList : []
          );
        } else {
          const message =
            response?.Result_Message ||
            "RCTI details could not be loaded. Check your connection and retry.";
          setLoadError({
            message,
            denied: /permission|access denied|unauthori[sz]ed|forbidden/i.test(
              message
            ),
          });
        }
        setLoadingPage(false);
      },
      dispatch,
      (error) => {
        const message =
          error?.Result_Message ||
          "RCTI details could not be loaded. Check your connection and retry.";
        setLoadError({
          message,
          denied: /permission|access denied|unauthori[sz]ed|forbidden/i.test(
            message
          ),
        });
        setLoadingPage(false);
      }
    );
  }, [rctiID, dispatch]);

  useEffect(() => {
    if (location.state?.paymentRecorded) {
      showDraftSavedMessage("Payment recorded successfully");
      navigate(location.pathname, { replace: true, state: null });
    } else if (location.state?.paymentError) {
      setSettlementResult({
        type: "failure",
        message: location.state.paymentError.message,
        draft: location.state.paymentError.draft,
      });
      setSettlementOpen(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    setLoginDetails(
      (current) =>
        current ||
        (() => {
          try {
            return JSON.parse(
              sessionStorage.getItem("LOGIN_DETAILS") || "null"
            );
          } catch {
            return null;
          }
        })()
    );
    loadDetails();
  }, [loadDetails]);

  const loadActivity = useCallback(() => {
    if (!rctiID || !loginDetails?.businessUserID) return;
    fetchActivityLog(
      {
        _id: rctiID,
        businessUserID: loginDetails.businessUserID,
        userID: loginDetails._id,
        details: "",
        sortingType: -1,
        sortingField: "dateAndTime",
        screenType: "RCTI",
        page: 1,
        limit: 10,
        filterValues: "",
        searchParameterList: ["dateAndTime", "name", "action", "details"],
        token: loginDetails.token,
      },
      (response) => {
        if (response?.Result_Status) {
          setActivityRows(response?.Result_Output?.docs || []);
          setActivityLoadError(false);
        } else {
          setActivityLoadError(true);
        }
      },
      dispatch
    );
  }, [
    rctiID,
    loginDetails?.businessUserID,
    loginDetails?._id,
    loginDetails?.token,
    dispatch,
  ]);

  useEffect(() => {
    loadActivity();
  }, [loadActivity]);

  const organisation =
    values.buyerIdentity?.name ||
    values.businessName ||
    loginDetails?.businessName ||
    loginDetails?.businessUserName ||
    "Your business";
  const currentUser =
    `${loginDetails?.firstName || ""} ${loginDetails?.lastName || ""}`.trim() ||
    "You";
  const currencyCode = String(
    [
      values.currencyCode,
      loginDetails?.currencyCode,
      values.currencyValue,
    ].find((value) => /^[A-Z]{3}$/i.test(String(value || ""))) || "AUD"
  ).toUpperCase();
  const money = (amount) =>
    new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: currencyCode,
    }).format(moneyValue(amount));
  const total = moneyValue(values.finalAmount ?? values.totalAmount);
  const paidAmount = moneyValue(values.paymentAmount || values.amountPaid);
  const outstanding = Math.max(
    0,
    moneyValue(values.payablePendingAmount ?? total - paidAmount)
  );
  const rawStatus = String(values.rctiStatus || "Unpaid").toLowerCase();
  const isVoided = rawStatus.includes("void");
  const recordedPaid = paymentHistory.length
    ? paymentHistory.reduce(
        (sum, payment) =>
          sum + moneyValue(payment.paymentAmount || payment.amount),
        0
      )
    : paidAmount;
  const isPartPaid =
    !isVoided &&
    (rawStatus.includes("part") ||
      ((paidAmount > 0 || recordedPaid > 0) && outstanding > 0));
  const isPaid =
    !isVoided &&
    !isPartPaid &&
    (rawStatus === "paid" ||
      (total > 0 && outstanding <= 0 && (paidAmount > 0 || recordedPaid > 0)));
  const isDraft =
    !isVoided &&
    !values.issuedAt &&
    !isPaid &&
    !isPartPaid &&
    Number(values.emailSent || 0) <= 0;
  const statusLabel = isVoided
    ? "Voided"
    : isPaid
    ? "Paid"
    : isPartPaid
    ? "Part-paid"
    : isDraft
    ? "Draft"
    : "Unpaid";
  const statusClass = statusLabel.toLowerCase().replace(/\s+/g, "-");

  const revisions = [...(values.versions || [])].sort(
    (a, b) => Number(a.version) - Number(b.version)
  );
  const original = revisions[0]?.document || values;
  const originalTotal = moneyValue(original.finalAmount ?? total);
  const adjusted = !isDraft && revisions.length > 0;
  const adjustmentDelta =
    (Math.round(total * 100) - Math.round(originalTotal * 100)) / 100;
  const signedMoney = (value) =>
    `${value < 0 ? "−" : value > 0 ? "+" : ""}${money(Math.abs(value))}`;
  const supplierName =
    values.supplierIdentity?.name ||
    values.supplierCompanyName ||
    values.supplierName ||
    "Supplier";
  const recipientEmail =
    values.customerEmailAddress ||
    values.supplierEmail ||
    values.supplierIdentity?.emailAddress ||
    values.emailAddress ||
    "Not recorded";
  const recordedDueDate = values.dueDate || null;
  const deliveryAt = values.lastEmailSentDate || values.emailSentDate || null;

  const goToActivity = () => navigate(`/rcti/activityLog/${rctiID}`);
  const recordPayment = () => {
    setSettlementResult(null);
    setSettlementOpen(true);
  };
  const continueToSettlement = (draft) => {
    setSettlementOpen(false);
    navigate(`/rcti/edit/${rctiID}`, {
      state: {
        openPaymentDetails: true,
        paymentFlowReturn: true,
        settlementDraft: draft,
      },
    });
  };
  const openCommunications = async () => {
    const userID = String(
      sessionStorage.getItem("USER_ID") || loginDetails?._id || ""
    ).replaceAll('"', "");
    getEmailAndTemplateForQuotation(
      {
        screenType: "RCTI",
        contactID: values.supplierID,
        selectedID: rctiID,
        userID,
      },
      (response) => {
        if (response?.Result_Status) {
          setTemplateValues(response.Result_Output);
          setCommunicationPhase("review");
          setCommunicationOpen(true);
        } else {
          showResMsg(
            response?.Result_Message || "Could not load supplier email details",
            2200,
            false
          );
        }
      },
      dispatch
    );
  };
  const sendCommunication = (emailData) => {
    setCommunicationPhase("issuing");
    const payload = new FormData();
    payload.append("selectedID", rctiID);
    payload.append("userEmailAddress", emailData.userEmailAddress || "");
    payload.append("customerEmailAddress", [
      ...(emailData.customerEmailAddress || []),
    ]);
    payload.append(
      "emailTemplate",
      JSON.stringify(emailData.emailTemplate || {})
    );
    payload.append("sendMeCopy", emailData.sendMeCopy);
    payload.append("file", emailData.uploadDocument);
    payload.append("fileName", new Date());
    payload.append(
      "businessUserID",
      loginDetails?.businessUserID || values.businessUserID || ""
    );
    return new Promise((resolve) => resendEmailRcti(
      payload,
      (response) => {
        resolve({ success: Boolean(response?.Result_Status), message: response?.Result_Message });
        if (response?.Result_Status) {
          setCommunicationPhase("sent");
          setCommunicationOpen(false);
          showDraftSavedMessage("RCTI sent successfully");
          loadDetails();
          loadActivity();
        } else {
          setCommunicationPhase("failed");
          showResMsg(
            response?.Result_Message || "RCTI could not be sent",
            2200,
            false
          );
        }
      },
      dispatch
    ));
  };
  const downloadPdf = () =>
    downloadRCTI(
      { _id: rctiID },
      (response) =>
        savePdfResponse(response, `${values.rctiNumber || "RCTI"}.pdf`),
      dispatch
    );

  const saveInternalNote = () => {
    if (!internalNote.trim() || savingNote) return;
    setSavingNote(true);
    saveNotes(
      {
        _id: rctiID,
        businessUserID: loginDetails?.businessUserID,
        userID: loginDetails?._id,
        details: internalNote.trim(),
        screenType: "RCTI",
        token: loginDetails?.token,
      },
      (response) => {
        setSavingNote(false);
        if (response?.Result_Status) {
          setInternalNote("");
          loadActivity();
        }
        showResMsg(
          response?.Result_Message || "Note saved",
          1600,
          Boolean(response?.Result_Status)
        );
      },
      dispatch
    );
  };

  const subtotalExcludingGst = total - moneyValue(values.totalGSTAmount);
  const gstRates = [
    ...new Set(
      items
        .map((item) =>
          String(item.gst || "")
            .trim()
            .replace(/^GST\s*/i, "")
            .replace(/%$/, "")
        )
        .filter((rate) => /\d/.test(rate))
        .map((rate) => `${rate}%`)
    ),
  ];
  const gstSummaryLabel = gstRates.length
    ? `GST ${gstRates.join(", ")} (included)`
    : "GST included";
  const emailSent = Number(values.emailSent || 0) > 0;
  const paymentHistoryError =
    values.paymentHistoryError || values.paymentHistoryMessage || "";

  if (loadingPage) {
    return (
      <div
        className={`invoice-flow-shell invoice-detail-shell rcti-detail-shell${
          darkMode ? " dark" : ""
        }`}
      >
        <DashboardNavigation
          open={navOpen}
          onClose={() => setNavOpen(false)}
          activePath="/rcti"
          organisation={organisation}
          role={loginDetails?.roleName || "Owner"}
        />
        <div className="invoice-flow-main">
          <AppHeader
            searchPlaceholder="Search RCTIs, suppliers, contacts"
            onOpenNav={() => setNavOpen(true)}
            userName={currentUser}
          />
          <main className="invoice-detail-page rcti-detail-page rcti-issued-journey">
            <PageSkeleton
              type="detail"
              variant="document"
              label="Loading RCTI details…"
            />
          </main>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div
        className={`invoice-flow-shell invoice-detail-shell rcti-detail-shell${
          darkMode ? " dark" : ""
        }`}
      >
        <DashboardNavigation
          open={navOpen}
          onClose={() => setNavOpen(false)}
          activePath="/rcti"
          organisation={organisation}
          role={loginDetails?.roleName || "Owner"}
        />
        <div className="invoice-flow-main">
          <AppHeader
            onOpenNav={() => setNavOpen(true)}
            userName={currentUser}
          />
          <main className="invoice-detail-page rcti-detail-page rcti-issued-journey">
            <div className="invoice-detail-orgbar">
              <DebitNoteResponsiveHeader
                organisation={organisation}
                role={loginDetails?.roleName || "Owner"}
              />
            </div>
            <section
              className={`rcti-load-error${loadError.denied ? " denied" : ""}`}
              role="alert"
            >
              <LuTriangleAlert />
              <div>
                <h1 className="type-h1">
                  {loadError.denied
                    ? "Access denied"
                    : "RCTI details unavailable"}
                </h1>
                <p>
                  {loadError.denied
                    ? "Your current role cannot view or update this RCTI. Ask an organisation administrator to grant access."
                    : loadError.message}
                </p>
              </div>
              {!loadError.denied && (
                <button type="button" onClick={loadDetails}>
                  <LuRefreshCw />
                  Retry load
                </button>
              )}
              <button type="button" onClick={() => navigate("/rcti")}>
                Back to RCTIs
              </button>
            </section>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`invoice-flow-shell invoice-detail-shell rcti-detail-shell${
        darkMode ? " dark" : ""
      }`}
    >
      <DashboardNavigation
        open={navOpen}
        onClose={() => setNavOpen(false)}
        activePath="/rcti"
        organisation={organisation}
        role={loginDetails?.roleName || "Owner"}
      />
      <div className="invoice-flow-main">
        <AppHeader
          searchPlaceholder="Search RCTIs, suppliers, contacts"
          onOpenNav={() => setNavOpen(true)}
          userName={currentUser}
        />
        <main className="invoice-detail-page rcti-detail-page rcti-issued-journey">
          <div className="invoice-detail-orgbar">
            <DebitNoteResponsiveHeader
              organisation={organisation}
              role={loginDetails?.roleName || "Owner"}
            />
          </div>
          <button
            className="invoice-flow-back rcti-detail-back"
            type="button"
            onClick={() => navigate("/rcti")}
          >
            <LuArrowLeft />
            Back to RCTIs
          </button>
          <header className="invoice-detail-header rcti-detail-header">
            <div>
              <h1 className="type-h1">{values.rctiNumber || "RCTI details"}</h1>
              <p>
                {supplierName} ·{" "}
                {isDraft
                  ? "Draft"
                  : `Issued ${dateValue(
                      original.issuedAt || original.rctiDate
                    )}`}
                {recordedDueDate ? ` · Due ${dateValue(recordedDueDate)}` : ""}
              </p>
            </div>
          </header>

          <div className="rcti-detail-context">
            <LuCircleCheck aria-hidden="true" />
            <div>
              <span className="rcti-type-emphasis">
                {adjusted
                  ? "Adjustment applied — original RCTI preserved"
                  : isDraft
                  ? "Draft saved · Not issued"
                  : isPaid
                  ? "RCTI paid"
                  : isPartPaid
                  ? "Part-paid RCTI"
                  : isVoided
                  ? "RCTI voided"
                  : `${values.rctiNumber || "RCTI"} successfully issued`}
              </span>
              <span>
                {adjusted
                  ? `Version ${values.version || 2} ${
                      adjustmentDelta < 0 ? "decreases" : "increases"
                    } your payable by ${money(
                      Math.abs(adjustmentDelta)
                    )}. Adjusted outstanding is ${money(
                      outstanding
                    )}; the original issued total remains ${money(
                      originalTotal
                    )} in history.`
                  : isDraft
                  ? "This RCTI has not been sent. Review supplier details and issue it when ready."
                  : isPaid || isPartPaid
                  ? `Supplier delivery and payment information for ${
                      values.rctiNumber || "this RCTI"
                    }.`
                  : `Issued and locked ${rctiTimestamp(
                      values.issuedAt || values.rctiDate
                    )}. ${
                      values.rctiNumber || "RCTI"
                    } totals ${currencyCode} ${money(
                      total
                    )}. Delivery is tracked separately below.`}
              </span>
            </div>
            <span className="rcti-detail-context-reference">
              <LuExternalLink />
              Supplier reference · {values.supplierReference || "—"}
            </span>
            {isDraft && (
              <button
                className="rcti-detail-action primary"
                type="button"
                onClick={openCommunications}
              >
                <LuSend />
                Issue RCTI
              </button>
            )}
          </div>
          <div className="rcti-issued-toolbar">
            <div>
              <span className="rcti-status status-draft">
                {isDraft
                  ? "Draft · Not issued"
                  : isVoided
                  ? "Voided"
                  : "Issued · Locked"}
              </span>
              <span className={`rcti-status status-${statusClass}`}>
                {statusLabel}
              </span>
              {adjusted && (
                <span className="rcti-status status-draft">Adjusted</span>
              )}
            </div>
            <div className="invoice-editor-actions">
              <button type="button" onClick={downloadPdf}>
                <LuDownload />
                Download RCTI
              </button>
              {!isDraft && !isVoided && (
                <button type="button" onClick={openCommunications}>
                  <LuMail />
                  Resend RCTI
                </button>
              )}
              {!isDraft && !isVoided && (
                <button type="button" disabled={isPaid} onClick={recordPayment}>
                  <LuWallet />
                  Record payment
                </button>
              )}
              {!isVoided && (
                <button
                  className="primary"
                  type="button"
                  disabled={
                    !isDraft &&
                    Boolean(agreement && agreement.status !== "active")
                  }
                  onClick={() => navigate(`/rcti/edit/${rctiID}`)}
                >
                  <LuFileText />
                  {isDraft ? "Edit draft" : "Create adjustment"}
                </button>
              )}
              {(isPaid || isVoided) && (
                <button type="button" onClick={goToActivity}>
                  <LuHistory />
                  View history
                </button>
              )}
            </div>
          </div>
          <div className="rcti-detail-layout">
            <div className="rcti-issued-document-column">
              <RCTIDocument
                issuedDetail
                agreement={agreement}
                values={values}
                items={items}
                documents={[]}
                showAttachments={false}
                organisation={organisation}
                rctiID={rctiID}
                loadingPage={loadingPage}
                currencyCode={currencyCode}
                subtotalExcludingGst={subtotalExcludingGst}
                gstSummaryLabel={gstSummaryLabel}
                total={total}
                money={money}
                dateValue={dateValue}
                supplierPath={
                  values.supplierID ? `/contacts/${values.supplierID}` : null
                }
              />

              {!isDraft && (
                <section className="invoice-flow-card invoice-state-panel rcti-detail-state-card">
                  <h2 className="type-h4">Issue &amp; adjustment history</h2>
                  <ol className="rcti-issued-history">
                    {revisions
                      .map((revision, index) => {
                        const next = revisions[index + 1]?.document || values;
                        const delta =
                          (Math.round(moneyValue(next.finalAmount) * 100) -
                            Math.round(
                              moneyValue(revision.document?.finalAmount) * 100
                            )) /
                          100;
                        return (
                          <li key={revision.version}>
                            <div>
                              <time>
                                {rctiTimestamp(
                                  next.issuedAt || revision.archivedAt
                                )}
                              </time>
                              <strong>{signedMoney(delta)}</strong>
                            </div>
                            <p>
                              <strong>
                                Adjustment · Version{" "}
                                {next.version || Number(revision.version) + 1}
                              </strong>{" "}
                              · {delta < 0 ? "Decrease" : "Increase"} in
                              consideration
                            </p>
                            <p>
                              {revision.adjustmentReason ||
                                next.adjustmentReason}
                            </p>
                            <Link
                              to={`/rcti/view/${rctiID}/versions/${revision.version}`}
                            >
                              View preserved version {revision.version}
                            </Link>
                          </li>
                        );
                      })
                      .reverse()}
                    <li>
                      <time>
                        {rctiTimestamp(original.issuedAt || original.rctiDate)}
                      </time>
                      <p>
                        <strong>{values.rctiNumber} issued and locked</strong> ·
                        Original total {money(originalTotal)}
                      </p>
                      <p>
                        Issued under the accepted RCTI agreement
                        {agreement
                          ? ` · ${agreementReference(agreement._id)} · v${
                              agreement.version
                            }`
                          : ""}
                        . Supplier delivery is recorded separately.
                      </p>
                    </li>
                  </ol>
                </section>
              )}
            </div>
            <aside className="rcti-detail-aside">
              <section className="invoice-flow-card invoice-total-card rcti-balance-card">
                <div>
                  <h2 className="type-h4">
                    {isDraft ? "Draft total" : "Outstanding payable"}
                  </h2>
                  <LuReceipt />
                </div>
                <span className="rcti-type-emphasis">
                  {money(isDraft ? total : outstanding)}
                </span>
                <p>
                  <span className={`rcti-status status-${statusClass}`}>
                    {isDraft ? "Not issued" : statusLabel}
                  </span>
                  {recordedDueDate ? ` Due ${dateValue(recordedDueDate)}` : ""}
                </p>
              </section>
              <section className="invoice-flow-card rcti-breakdown-card">
                <h2>Balance breakdown</h2>
                <dl className="rcti-c13-balances">
                  <div>
                    <dt>Original RCTI</dt>
                    <dd>{money(originalTotal)}</dd>
                  </div>
                  {adjusted && (
                    <div>
                      <dt>Adjustments · v{values.version || 2}</dt>
                      <dd>{signedMoney(adjustmentDelta)}</dd>
                    </div>
                  )}
                  <div>
                    <dt>Current payable</dt>
                    <dd>{money(total)}</dd>
                  </div>
                  <div>
                    <dt>Paid</dt>
                    <dd>{money(recordedPaid)}</dd>
                  </div>
                  <div className="rcti-c13-outstanding">
                    <dt>Outstanding {currencyCode}</dt>
                    <dd>{money(outstanding)}</dd>
                  </div>
                </dl>
                <p>
                  Payable to {supplierName}.{" "}
                  {recordedPaid > 0
                    ? `${money(recordedPaid)} in recorded payments.`
                    : "No payments recorded."}
                </p>
              </section>

              <section className="invoice-flow-card invoice-settings-card rcti-delivery-card">
                <h2 className="type-h4">Supplier delivery</h2>
                <span
                  className={`rcti-status ${
                    emailSent ? "status-sent" : "status-draft"
                  }`}
                >
                  {emailSent ? "Sent" : "Not sent"}
                </span>
                <p>
                  To {values.supplierIdentity?.name || supplierName}
                  <br />
                  {recipientEmail}
                </p>
                <p>
                  {emailSent
                    ? `RCTI email accepted${
                        deliveryAt
                          ? ` ${rctiTimestamp(deliveryAt)}`
                          : "; delivery time not recorded"
                      }`
                    : "No successful email submission recorded."}
                  {!isDraft && (
                    <>
                      <br />
                      Issue completed {rctiTimestamp(values.issuedAt)}
                    </>
                  )}
                </p>
                {!isVoided && (
                  <button
                    type="button"
                    className="rcti-detail-action"
                    onClick={openCommunications}
                  >
                    <LuMail />
                    {isDraft ? "Issue RCTI" : "Resend RCTI"}
                  </button>
                )}
                <button
                  type="button"
                  className="rcti-c13-text-action"
                  onClick={goToActivity}
                >
                  View delivery history
                </button>
              </section>
              {values.rctiAgreementID && (
                <section className="invoice-flow-card rcti-detail-state-card">
                  <h2 className="type-h4">Agreement reference</h2>
                  <strong className="rcti-c13-agreement-ref">
                    {agreement && (
                      <span className="rcti-status status-sent">
                        {agreement.status}
                      </span>
                    )}
                    {agreementReference(values.rctiAgreementID)}
                    {agreement ? ` · v${agreement.version}` : ""}
                  </strong>
                  {agreement && (
                    <>
                      <p>
                        Buyer signed {rctiTimestamp(agreement.buyerSignedAt)}
                      </p>
                      <p>
                        Supplier accepted{" "}
                        {rctiTimestamp(agreement.supplierSignedAt)}
                      </p>
                      <p>Effective on acceptance · No fixed expiry</p>
                      <p>{agreement.scope}</p>
                    </>
                  )}
                  <Link to={`/rcti-agreements/${values.rctiAgreementID}`}>
                    View agreement
                  </Link>
                  {agreement && isSignedAgreement(agreement) && (
                    <button
                      type="button"
                      className="rcti-detail-action"
                      onClick={async () => {
                        try {
                          const result = await agreementRequest(
                            `/${agreement._id}/pdf`
                          );
                          savePdfResponse(
                            { data: result.pdf },
                            result.filename
                          );
                        } catch (error) {
                          showResMsg(error.message, 2500, false);
                        }
                      }}
                    >
                      Download accepted agreement
                    </button>
                  )}
                  {agreement && agreement.status !== "active" && (
                    <p>
                      New issues and adjustments are unavailable under this
                      agreement. Existing RCTIs remain in history.
                    </p>
                  )}
                </section>
              )}
              {adjusted && (
                <section className="invoice-flow-card rcti-issued-adjustment-card">
                  <h2>Issued adjustment</h2>
                  <p>
                    Version {values.version || 2} ·{" "}
                    {signedMoney(adjustmentDelta)}
                    <br />
                    {rctiTimestamp(values.issuedAt)}
                  </p>
                  <p>{values.adjustmentReason}</p>
                  <button
                    type="button"
                    className="rcti-detail-action"
                    onClick={downloadPdf}
                  >
                    Download adjustment
                  </button>
                </section>
              )}
              <section className="invoice-flow-card rcti-supporting-documents">
                <h2>Supporting documents</h2>
                {documents.length ? (
                  documents.map((doc, index) => (
                    <button
                      type="button"
                      className="rcti-document-link"
                      key={doc._id || index}
                      onClick={() => downloadRctiAttachment(doc)}
                    >
                      <LuFileText />
                      <span>
                        {doc.documentName ||
                          doc.documentFileName ||
                          `Attachment ${index + 1}`}
                      </span>
                      <LuDownload />
                    </button>
                  ))
                ) : (
                  <p>No supporting documents added.</p>
                )}
              </section>
            </aside>
          </div>

          {!isDraft && (
            <div className="rcti-detail-lower-panels rcti-payment-history-panel">
              <section className="invoice-flow-card invoice-state-panel rcti-detail-state-card">
                  <h2 className="type-h4">Payment history</h2>
                  <div className="rcti-state-financial-row">
                    <span>Payments made</span>
                    <span className="rcti-type-emphasis">
                      {money(recordedPaid)}
                    </span>
                  </div>
                  <hr />
                  {paymentHistoryError ? (
                    <div className="rcti-history-load-error" role="alert">
                      <span>{paymentHistoryError}</span>
                      <button type="button" onClick={loadDetails}>
                        <LuRefreshCw />
                        Retry history
                      </button>
                    </div>
                  ) : paymentHistory.length ? (
                    <div className="invoice-payment-list">
                      {paymentHistory.map((payment, index) => {
                        const exceptionLabel = String(
                          payment.status || payment.paymentStatus || ""
                        );
                        const exceptional = /revers|duplicat|overpay/i.test(
                          exceptionLabel
                        );
                        return (
                          <article key={payment._id || index}>
                            <span className="rcti-type-emphasis">
                              {money(payment.paymentAmount || payment.amount)}
                            </span>
                            <span>
                              {payment.paymentMode ||
                                payment.paymentMethod ||
                                payment.paymentType ||
                                "Payment"}
                              {exceptional && (
                                <span className="rcti-payment-exception-status">
                                  {exceptionLabel}
                                </span>
                              )}
                            </span>
                            <small>
                              {rctiTimestamp(
                                payment.paymentDate || payment.createdAt
                              )}
                              {payment.paymentTransactionID ||
                              payment.referenceNumber
                                ? ` · ${
                                    payment.paymentTransactionID ||
                                    payment.referenceNumber
                                  }`
                                : ""}
                            </small>
                            {exceptional && (
                              <small className="rcti-exception-copy">
                                This payment exception is shown as returned by
                                the RCTI service; no settlement values were
                                changed.
                              </small>
                            )}
                          </article>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="rcti-payment-empty-copy">
                      <span>
                        {recordedPaid
                          ? "Payment total recorded"
                          : "No payments recorded"}
                      </span>
                      <p>
                        Record a payment when money has been paid to the
                        supplier.
                      </p>
                    </div>
                  )}
                  {!isVoided && (
                    <button
                      className="rcti-detail-action"
                      disabled={isPaid}
                      type="button"
                      onClick={recordPayment}
                    >
                      <LuWallet />
                      Record payment
                    </button>
                  )}
              </section>
            </div>
          )}

          <section className="invoice-flow-card invoice-history-card invoice-history-card-live rcti-detail-history">
              <div className="invoice-history-heading">
                <div>
                  <h2 className="type-h5">
                    <LuHistory />
                    RCTI history &amp; notes
                  </h2>
                  <p>
                    Recorded activity · Internal notes are only visible to your
                    team.
                  </p>
                </div>
                <button
                  type="button"
                  className="invoice-inline-action"
                  onClick={goToActivity}
                >
                  View full history
                </button>
              </div>
              <div className="invoice-private-note">
                <LuLockKeyhole />
                <div>
                  <span className="rcti-type-emphasis">
                    Only visible to your team
                  </span>
                  <span>
                    History and internal notes never appear on the supplier
                    document or PDF.
                  </span>
                </div>
              </div>
              <div className="rcti-history-layout">
                <div className="rcti-history-feed">
                  {activityRows.length ? (
                    activityRows.slice(0, 3).map((activity, index) => (
                      <article key={activity._id || index}>
                        <div>
                          <span className="rcti-type-emphasis">
                            {activity.action || activity.name || "RCTI updated"}
                          </span>
                          <time>{activity.dateAndTime || ""}</time>
                        </div>
                        <p>{activity.details || ""}</p>
                      </article>
                    ))
                  ) : (
                    <p className="rcti-history-empty">
                      {activityLoadError
                        ? "Activity history could not be loaded."
                        : "No activity has been recorded for this RCTI."}
                    </p>
                  )}
                </div>
                <div className="rcti-note-composer">
                  <label htmlFor="rcti-internal-note">
                    Add an internal note
                  </label>
                  <textarea
                    id="rcti-internal-note"
                    value={internalNote}
                    onChange={(event) => setInternalNote(event.target.value)}
                    placeholder="Write a note for your team..."
                  />
                  <small>Only visible to your team</small>
                  <button
                    type="button"
                    className="rcti-detail-action primary"
                    disabled={!internalNote.trim() || savingNote}
                    onClick={saveInternalNote}
                  >
                    {savingNote ? "Saving…" : "Add note"}
                  </button>
                </div>
              </div>
              <p className="rcti-history-footnote">
                Internal notes are private and are not included in supplier
                emails or the RCTI PDF.
              </p>
          </section>
          <footer className="rcti-issued-footer">
            <Link to="/rcti">Back to RCTIs</Link>
            {!isDraft && !isVoided && (
              <button
                className="rcti-detail-action primary"
                disabled={Boolean(agreement && agreement.status !== "active")}
                onClick={() => navigate(`/rcti/edit/${rctiID}`)}
              >
                Create adjustment
              </button>
            )}
          </footer>
        </main>
      </div>
      <SendInvoiceModal
        open={communicationOpen}
        documentLabel="RCTI"
        messageOnly
        invoiceNumber={values.rctiNumber}
        customerName={values.supplierCompanyName || values.supplierName || "Supplier"}
        customerEmail={templateValues?.customerEmailAddress?.[0] || values.supplierEmail || ""}
        invoiceDate={values.rctiDate}
        finalAmount={Number(values.finalAmount || 0)}
        currency={currencyCode}
        organisation={organisation}
        invoiceStatus="Issued"
        templateData={templateValues}
        onSend={sendCommunication}
        onClose={() => {
          if (communicationPhase !== "issuing") setCommunicationOpen(false);
        }}
        onViewInvoice={() => setCommunicationOpen(false)}
        darkMode={darkMode}
        mode="resend"
      />
      <RCTISettlementDialog
        open={settlementOpen}
        dark={darkMode}
        values={values}
        currencyCode={currencyCode}
        outstanding={outstanding}
        paidAmount={recordedPaid}
        result={settlementResult}
        onClose={() => {
          setSettlementOpen(false);
          setSettlementResult(null);
        }}
        onContinue={continueToSettlement}
        onView={() => {
          setSettlementOpen(false);
          setSettlementResult(null);
        }}
      />
    </div>
  );
}

export default ViewRCTI;
